import {useFormContext} from './useFormContext';

import type {FormValue} from '../sharedTypes/formHelperTypes';

/**
 * Custom hook for managing form field editing functionality within a form context.
 * This hook provides access to a form field's current value and a handler function to update that value.
 * It must be used within a FormProvider component to access the form context properly.
 *
 * @param  name The name identifier of the form field to manage.
 * @returns An object containing the field's current value and a change handler function.
 *
 * @throws {Error} Throws an error if used outside of a FormProvider context.
 *
 * @example
 * ```tsx
 * const { value, handleFieldChange } = useFormEditFunction<string>('username');
 *
 * // Update the field value
 * handleFieldChange('newUsername');
 *
 * // Access current value
 * console.log(value); // Current username value
 * ```
 */
export const useFormEditFunction = <T extends FormValue>(name: string) => {
    const formContext = useFormContext();

    if (!formContext) {
        throw new Error('useFormField must be used within a FormProvider');
    }

    const {form} = formContext;

    /**
     * Form field change handler function that updates the specified form field's value.
     * This function triggers a value update in the form context for the given field name.
     * It accepts various value types including strings, booleans, and string arrays to accommodate different form field types.
     *
     * @param value The new value to set for the form field. Can be a string, boolean, or array of strings.
     */
    const handleFieldChange = (value: string[] | boolean | string) => {
        form.changeValue(name, value);
    };

    const value = form.values[String(name)] as T | undefined;

    return {
        handleFieldChange,
        value
    };
};